/**
 * Default lab work-type / material selects for a catalog item.
 * Collapses in/out when "create lab request" is toggled.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse } from '@mui/material';
import { Box, FormControl, Grid, InputLabel, MenuItem, Select, SelectPlaceholder } from '@/shared/ui';
import {
  buildLabCatalogLabelMap,
  useLabMaterials,
  useLabWorkTypes,
} from '@/modules/labo/hooks/useLabCatalog';

interface LabCatalogDefaultsFieldsProps {
  labOrderEnabled: boolean;
  defaultLabWorkType: string;
  defaultLabMaterial: string;
  disabled?: boolean;
  onChange: (patch: {
    labOrderEnabled?: boolean;
    defaultLabWorkType?: string;
    defaultLabMaterial?: string;
  }) => void;
}

const LabCatalogDefaultsFields: React.FC<LabCatalogDefaultsFieldsProps> = ({
  labOrderEnabled,
  defaultLabWorkType,
  defaultLabMaterial,
  disabled = false,
  onChange,
}) => {
  const { t } = useTranslation('treatment');
  const workTypesQuery = useLabWorkTypes(false);
  const materialsQuery = useLabMaterials(false);
  const workTypes = workTypesQuery.data ?? [];
  const materials = materialsQuery.data ?? [];
  const workTypeLabels = buildLabCatalogLabelMap(workTypes);
  const materialLabels = buildLabCatalogLabelMap(materials);

  return (
    <Collapse in={labOrderEnabled} timeout="auto" unmountOnExit appear={false}>
      {/* Padding lives inside Collapse so the gap to Description animates away instead of jumping. */}
      <Box sx={{ pb: 2.5 }}>
        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth disabled={disabled}>
              <InputLabel shrink>{t('fields.defaultLabWorkType')}</InputLabel>
              <Select
                value={defaultLabWorkType}
                label={t('fields.defaultLabWorkType')}
                displayEmpty
                notched
                disabled={disabled}
                renderValue={(selected) => {
                  const value = String(selected ?? '');
                  if (!value) {
                    return (
                      <SelectPlaceholder>
                        {t('catalog.placeholders.defaultLabWorkType')}
                      </SelectPlaceholder>
                    );
                  }
                  return workTypeLabels[value] ?? value;
                }}
                onChange={(e) => onChange({ defaultLabWorkType: String(e.target.value) })}
              >
                <MenuItem value="">{t('catalog.placeholders.defaultLabWorkType')}</MenuItem>
                {workTypes.map((type) => (
                  <MenuItem key={type.code} value={type.code}>
                    {type.name}
                  </MenuItem>
                ))}
                {defaultLabWorkType && !workTypeLabels[defaultLabWorkType] ? (
                  <MenuItem value={defaultLabWorkType}>{defaultLabWorkType}</MenuItem>
                ) : null}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth disabled={disabled}>
              <InputLabel shrink>{t('fields.defaultLabMaterial')}</InputLabel>
              <Select
                value={defaultLabMaterial}
                label={t('fields.defaultLabMaterial')}
                displayEmpty
                notched
                disabled={disabled}
                renderValue={(selected) => {
                  const value = String(selected ?? '');
                  if (!value) {
                    return (
                      <SelectPlaceholder>
                        {t('catalog.placeholders.defaultLabMaterial')}
                      </SelectPlaceholder>
                    );
                  }
                  return materialLabels[value] ?? value;
                }}
                onChange={(e) => onChange({ defaultLabMaterial: String(e.target.value) })}
              >
                <MenuItem value="">{t('catalog.placeholders.defaultLabMaterial')}</MenuItem>
                {materials.map((material) => (
                  <MenuItem key={material.code} value={material.code}>
                    {material.name}
                  </MenuItem>
                ))}
                {defaultLabMaterial && !materialLabels[defaultLabMaterial] ? (
                  <MenuItem value={defaultLabMaterial}>{defaultLabMaterial}</MenuItem>
                ) : null}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>
    </Collapse>
  );
};

export default LabCatalogDefaultsFields;
